/**
 * Graphe de prédictions, en SVG écrit à la main, et sans domaine.
 *
 * Ce module ne sait pas ce qu'il trace. Il reçoit des courbes, un formateur
 * d'abscisse et des libellés ; il ne mentionne ni ombre, ni date, ni hauteur de
 * chute. C'est ce qui lui permet de servir les deux expériences — et la
 * suivante — sans qu'aucune ligne soit recopiée.
 *
 * Ce qu'il continue de garantir, en revanche, ne dépend d'aucun domaine :
 *
 * - **aucune mesure n'y entre.** Toutes les séries sont des prédictions, et
 *   c'est écrit en toutes lettres dans la mise en garde. La confrontation aux
 *   mesures a son propre graphe (`eratosthenes-chart.ts`), qui sait refuser.
 * - **la forme distingue les séries, pas la couleur** (CLAUDE.md §3). Chaque
 *   courbe porte des symboles distincts, repris dans sa légende.
 *   Le trait plein indique toujours une prédiction.
 * - **un trou reste un trou.** Un point sans valeur interrompt le tracé au lieu
 *   d'être relié : un modèle qui ne prédit rien ne doit pas paraître prédire
 *   une droite.
 */

import type { ChartData, Series, SeriesPoint } from "../experiment/types.js";
import { fr } from "../i18n/fr.js";
import { escape, escapeAttribute, formatNumber } from "./format.js";
import { plotPaths, plotMarkers, MODEL_SYMBOLS } from "./plot.js";
import { linearTicks, tickDecimals } from "./axis.js";

const WIDTH = 720;
const HEIGHT = 380;
const MARGIN = { top: 32, right: 24, bottom: 48, left: 64 } as const;
/** Bornes de lisibilité, pas de préférence : au-delà, les libellés se recouvrent. */
const X_TICKS = 7;
const Y_TICKS = 5;
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;

interface Scale {
  readonly toX: (x: number) => number;
  readonly toY: (y: number) => number;
  readonly minX: number;
  readonly maxX: number;
  readonly maxY: number;
}

function defined(point: SeriesPoint): point is SeriesPoint & { readonly y: number } {
  return point.y !== undefined;
}

function scaleOf(series: readonly Series[]): Scale | undefined {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const one of series) {
    for (const point of one.points) {
      if (!defined(point)) continue;
      xs.push(point.x);
      ys.push(point.y);
    }
  }
  if (xs.length === 0) return undefined;

  // Les bornes se prennent sur **toutes** les courbes, pas sur la première :
  // deux modèles peuvent ne pas couvrir le même intervalle, et c'est souvent
  // là qu'ils se séparent — deux chutes qui ne finissent pas ensemble, par
  // exemple. Cadrer sur l'une d'elles amputerait l'autre.
  const minX = xs.reduce((low, x) => Math.min(low, x), Infinity);
  const maxX = xs.reduce((high, x) => Math.max(high, x), -Infinity);
  const maxY = ys.reduce((high, y) => Math.max(high, y), -Infinity);
  const spanX = maxX - minX || 1;
  const spanY = maxY || 1;

  return {
    toX: (x) => MARGIN.left + ((x - minX) / spanX) * PLOT_WIDTH,
    toY: (y) => MARGIN.top + PLOT_HEIGHT - (y / spanY) * PLOT_HEIGHT,
    minX,
    maxX,
    maxY,
  };
}

/**
 * Un tracé par tronçon continu.
 *
 * Un modèle peut prédire sur une partie du domaine seulement. Relier les deux
 * bords du trou dessinerait une prédiction qu'il n'a jamais faite.
 */

function group(one: Series, rank: number, scale: Scale): string {
  const paths = plotPaths(one.points, scale);
  if (paths === "") return "";
  return [
    `<g data-nature="predicted" data-model="${escapeAttribute(one.id)}"`,
    ` data-series="${rank}" class="verlet-series verlet-series--predicted">`,
    paths,
    plotMarkers(one.points, rank, scale),
    `</g>`,
  ].join("");
}

function axes(data: ChartData, scale: Scale): string {
  const xTicks = linearTicks(scale.minX, scale.maxX, X_TICKS)
    .map((x) => {
      const px = scale.toX(x);
      return [
        `<text x="${px.toFixed(2)}" y="${(MARGIN.top + PLOT_HEIGHT + 20).toFixed(2)}"`,
        ` text-anchor="middle" class="verlet-tick">${escape(data.formatX(x))}</text>`,
      ].join("");
    })
    .join("");

  const decimals = tickDecimals(scale.maxY);
  const yTicks = linearTicks(0, scale.maxY, Y_TICKS)
    .map((y) => {
      const py = scale.toY(y);
      return [
        `<line x1="${MARGIN.left}" y1="${py.toFixed(2)}"`,
        ` x2="${MARGIN.left + PLOT_WIDTH}" y2="${py.toFixed(2)}" class="verlet-gridline" />`,
        `<text x="${MARGIN.left - 8}" y="${(py + 4).toFixed(2)}" text-anchor="end"`,
        ` class="verlet-tick">${escape(formatNumber(y, decimals))}</text>`,
      ].join("");
    })
    .join("");

  return [
    `<g class="verlet-axes">`,
    yTicks,
    `<line x1="${MARGIN.left}" y1="${MARGIN.top}" x2="${MARGIN.left}" y2="${MARGIN.top + PLOT_HEIGHT}" />`,
    `<line x1="${MARGIN.left}" y1="${MARGIN.top + PLOT_HEIGHT}" x2="${MARGIN.left + PLOT_WIDTH}" y2="${MARGIN.top + PLOT_HEIGHT}" />`,
    `<text x="8" y="${MARGIN.top - 12}" class="verlet-axis-label">${escape(data.yAxisLabel)}</text>`,
    xTicks,
    `</g>`,
  ].join("");
}

/**
 * Rien à tracer n'est pas une panne.
 *
 * La nuit polaire, une hypothèse manquante : ce sont des réponses, et un écran
 * vide les effacerait. On dit donc ce qui se passe, avec les mots que
 * l'expérience a choisis.
 */
function empty(data: ChartData): string {
  return [
    `<div class="verlet-refusal" role="note">`,
    `<p class="verlet-refusal__title">${escape(data.emptyTitle)}</p>`,
    `<p>${escape(data.emptyExplanation)}</p>`,
    `</div>`,
  ].join("");
}

export function renderSeriesChart(data: ChartData): string {
  const scale = scaleOf(data.series);
  if (scale === undefined) return empty(data);

  const legend = data.series
    .map(
      (one, rank) =>
        `<li data-nature="predicted" data-model="${escapeAttribute(one.id)}"` +
        ` data-series="${rank}">${MODEL_SYMBOLS[rank % MODEL_SYMBOLS.length]} ${escape(one.label)}</li>`,
    )
    .join("");

  return [
    `<figure class="verlet-chart">`,
    `<figcaption class="verlet-caveat" role="note">${escape(data.caveat)}</figcaption>`,
    `<div class="verlet-chart__viewport" role="region" tabindex="0"`,
    ` aria-label="${escapeAttribute(data.title)}">`,
    `<svg viewBox="0 0 ${WIDTH} ${HEIGHT}" role="img"`,
    ` aria-labelledby="verlet-pred-title verlet-pred-desc">`,
    `<title id="verlet-pred-title">${escape(data.title)}</title>`,
    `<desc id="verlet-pred-desc">${escape(data.description)}</desc>`,
    axes(data, scale),
    data.series.map((one, rank) => group(one, rank, scale)).join(""),
    `</svg>`,
    `</div>`,
    `<ul class="verlet-legend">${legend}</ul>`,
    `</figure>`,
  ].join("");
}

/** La mise en garde est la même pour toute expérience : elle dit une règle, pas un domaine. */
export const PREDICTIONS_ONLY = fr.predictionsOnlyCaveat;
